import { OrderFormData } from '../types/Order';
import { Product } from '../types/Product';

export interface WhatsAppMessageOptions {
  phoneNumber?: string;
  apiKey?: string;
  includeProductDetails?: boolean;
  includeTimestamp?: boolean;
  customMessage?: string;
}

export class WhatsAppService {
  private static readonly DEFAULT_PHONE = "21653231142";
  private static readonly DEFAULT_API_KEY = "7243808";

  /**
   * Format order data for WhatsApp message
   */
  static formatOrderMessage(
    formData: OrderFormData, 
    total: number,
    options: WhatsAppMessageOptions = {}
  ): string {
    const { includeProductDetails = true, includeTimestamp = true } = options;

    let message = `🛒 طلبية جديدة من Totale Sécurité\n\n`;
    
    // Customer Information
    message += `👤 العميل: ${formData.name}\n`;
    message += `📞 الهاتف: ${formData.phone}\n`;
    message += `🏙️ المدينة: ${formData.city}\n`;
    message += `📍 العنوان: ${formData.address}\n\n`;
    
    // Products Information
    if (includeProductDetails) {
      message += `🛍️ المنتجات:\n`;
      formData.selectedProducts.forEach((product, index) => {
        message += `${index + 1}. ${product.arabicName} (${product.name})\n`;
        message += `   💰 السعر: ${product.price.toFixed(3)} د.ت\n`;
      });
      message += `\n`;
    } else {
      message += `🛍️ المنتجات: ${formData.selectedProducts.map(p => p.arabicName).join(', ')}\n`;
    }
    
    message += `📦 الكمية: ${formData.quantity}\n`;
    message += `💰 المجموع الكلي: ${total.toFixed(3)} د.ت\n\n`;
    
    // Additional Notes
    if (formData.notes && formData.notes.trim()) {
      message += `📝 ملاحظات: ${formData.notes}\n\n`;
    }
    
    // Service Information
    message += `✅ دفع عند الاستلام\n`;
    message += `🚚 توصيل مجاني\n`;
    message += `🛡️ ضمان سنة كاملة\n`;
    
    // Timestamp
    if (includeTimestamp) {
      const now = new Date();
      const timestamp = now.toLocaleString('ar-TN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      message += `\n📅 وقت الطلب: ${timestamp}`;
    }
    
    return message;
  }

  /**
   * Send WhatsApp message via CallMeBot API
   */
  static async sendOrderMessage(
    formData: OrderFormData,
    total: number,
    options: WhatsAppMessageOptions = {}
  ): Promise<boolean> {
    try {
      const phoneNumber = options.phoneNumber || this.DEFAULT_PHONE;
      const apiKey = options.apiKey || this.DEFAULT_API_KEY;
      
      const message = options.customMessage || this.formatOrderMessage(formData, total, options);
      
      const callMeBotUrl = `https://api.callmebot.com/whatsapp.php?phone=${phoneNumber}&text=${encodeURIComponent(message)}&apikey=${apiKey}`;
      
      // Send the message (no-cors mode for CallMeBot)
      await fetch(callMeBotUrl, { mode: 'no-cors' });
      
      return true;
    } catch (error) {
      console.error('Failed to send WhatsApp message:', error);
      return false;
    }
  }

  /**
   * Generate WhatsApp Web URL for direct messaging
   */
  static generateWhatsAppUrl(
    message: string,
    phoneNumber: string = this.DEFAULT_PHONE
  ): string {
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  }

  /**
   * Open WhatsApp with custom message
   */
  static openWhatsApp(message: string, phoneNumber?: string): void {
    const url = this.generateWhatsAppUrl(message, phoneNumber);
    window.open(url, '_blank');
  }

  /**
   * Generate quick contact message templates
   */
  static getQuickMessages(): Array<{ label: string; message: string }> {
    return [
      {
        label: 'استفسار عام',
        message: 'مرحبا! أريد معلومات عن منتجات الحماية للسيارات 🚗'
      },
      {
        label: 'السعر والتوصيل',
        message: 'مرحبا! كم سعر Block Pedal وكم يكلف التوصيل؟ 💰'
      },
      {
        label: 'التوافق مع السيارة',
        message: 'مرحبا! هل منتجكم متوافق مع سيارتي؟ 🚙'
      },
      {
        label: 'طلب فوري',
        message: 'مرحبا! أريد طلب Block Pedal الآن. كيف يمكنني ذلك؟ 🛒'
      },
      {
        label: 'خدمة العملاء',
        message: 'مرحبا! أحتاج مساعدة في خدمة العملاء 📞'
      }
    ];
  }

  /**
   * Validate Tunisian phone number
   */
  static isValidTunisianPhone(phone: string): boolean {
    // Remove spaces and special characters
    const cleaned = phone.replace(/[\s\-\(\)]/g, '');
    
    // Check for Tunisian mobile number patterns
    const patterns = [
      /^(\+216|216|0)?[2-9]\d{7}$/, // Standard format
      /^(\+216|216)?[2-9]\d{7}$/ // Without leading zero
    ];
    
    return patterns.some(pattern => pattern.test(cleaned));
  }

  /**
   * Format Tunisian phone number for display
   */
  static formatTunisianPhone(phone: string): string {
    const cleaned = phone.replace(/[\s\-\(\)]/g, '');
    
    // Remove country code if present
    const withoutCountry = cleaned.replace(/^(\+216|216)/, '');
    
    // Add formatting: XX XXX XXX
    if (withoutCountry.length === 8) {
      return withoutCountry.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3');
    }
    
    return phone; // Return original if can't format
  }
}

export const handleWhatsAppOrder = (formData: OrderFormData, product?: Product) => {
  const productInfo = product ? {
    name: product.name,
    price: product.price,
    total: product.price * formData.quantity
  } : null;

  const orderMessage = `
🚗 *Commande SecureCar Tunisia*

👤 Nom: ${formData.name}
📱 Téléphone: ${formData.phone}
📍 Adresse: ${formData.address}
🚙 Modèle de voiture: ${formData.carModel}
${productInfo ? `🛡️ Produit: ${productInfo.name}` : ''}
📦 Quantité: ${formData.quantity}
💰 Prix: ${productInfo ? productInfo.total : formData.quantity * 150} TND

🔒 Dispositif anti-vol pour voiture
✅ Paiement à la livraison
🚚 Livraison gratuite en Tunisie
`;
  
  const phoneNumber = "21653231142";
  const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(orderMessage)}`;
  window.open(whatsappURL, '_blank');
};

// Export utility functions for convenience
export const sendWhatsAppOrder = WhatsAppService.sendOrderMessage.bind(WhatsAppService);
export const openWhatsAppChat = WhatsAppService.openWhatsApp.bind(WhatsAppService);
export const formatOrderForWhatsApp = WhatsAppService.formatOrderMessage.bind(WhatsAppService);